import md5 from 'md5';
import { parse } from 'qs';
import Store from 'repatch';

import State from '../core/State';
import TiktokAdAccount from '../entity/TiktokAdAccount';
import ErrorPresenter from '../service/ErrorPresenter';
import MessageDispatcher from '../service/MessageDispatcher';
import TiktokAdWebApi from '../service/persistent/TiktokAdWebApi';
import { setUploadedAdPlatformKeys } from '../thunk/creativeSending';
import {
  decrement as decrementFullScreenSpinnerNecessity,
  increment as incrementFullScreenSpinnerNecessity,
  setLoadingMessage,
} from '../thunk/fullScreenSpinner';
import { setLastAccessedOrganization } from '../thunk/lastAccessedOrganization';
import {
  finishIntegratedTiktokCommunication,
  setIntegratedTiktokAdAccounts,
  setTiktokAdAccounts,
  setTiktokAdAccountTokenId,
  setTiktokUploadedCreative,
  startIntegratedTiktokAdAccounts,
} from '../thunk/tiktokAdAccount';
import { OrganizationDetailFragment } from './../generated/graphql';

class IntegrateTiktokAdAccountUsecase {
  private store: Store<State>;
  private errorPresenter: ErrorPresenter;
  private messageDispatcher: MessageDispatcher;
  private tiktokAdWebApi: TiktokAdWebApi;
  private appId: string;

  constructor({
    store,
    errorPresenter,
    tiktokAdWebApi,
    messageDispatcher,
    appId,
  }: {
    store: Store<State>;
    errorPresenter: ErrorPresenter;
    tiktokAdWebApi: TiktokAdWebApi;
    messageDispatcher: MessageDispatcher;
    appId: string;
  }) {
    this.store = store;
    this.errorPresenter = errorPresenter;
    this.tiktokAdWebApi = tiktokAdWebApi;
    this.messageDispatcher = messageDispatcher;
    this.appId = appId;
  }

  public fetchAd = () => {
    const state: State = this.store.getState();
    const query = parse(state.location.search.slice(1));
    const organizationId: string | null = query.org || null;
    const csrf = md5(new Date().getTime().toString());

    window.localStorage.removeItem('tiktok-collector-fetching');
    window.localStorage.removeItem('tiktok-collector-pathname');
    window.localStorage.setItem('tiktok-collector-csrf', csrf);
    window.localStorage.setItem('tiktok-collector-pathname', state.location.pathname);
    window.localStorage.setItem('tiktok-collector-target-orgId', organizationId || '');
    window.location.href = `https://ads.tiktok.com/marketing_api/auth?app_id=${this.appId}&state=${csrf}&redirect_uri=${window.location.origin}&scope=tiktokads`;
  };

  /**
   * TiktokAdAccount連携のログイン後、リダイレクトによってページ遷移してきた時のみ呼ばれる
   */
  public fetchAccessibleTiktokAdAccounts = async (): Promise<boolean | void> => {
    if (location.href.match(/tiktokAdAuthCode=/)) {
      const query = parse(location.search.substr(1));
      const authCode = query.tiktokAdAuthCode;
      const fetching = window.localStorage.getItem('tiktok-collector-fetching') == 'fetching';
      const pathname = window.localStorage.getItem('tiktok-collector-pathname') || '';
      if (authCode) {
        this.store.dispatch(incrementFullScreenSpinnerNecessity());
        try {
          let tokenId = null;
          if (!fetching) {
            window.localStorage.setItem('tiktok-collector-fetching', 'fetching');
            tokenId = await this.tiktokAdWebApi.registerTiktokAccount(authCode, query.org);
          }
          if (tokenId && pathname.match(/settings/)) {
            this.store.dispatch(setTiktokAdAccountTokenId(tokenId));
            // 連携可能なアカウント一覧
            const accessibleTiktokAdAccounts = await this.tiktokAdWebApi.getAllAccessibleTiktokAdAccounts(
              this.getCurrentOrganization().id,
              tokenId,
            );
            this.store.dispatch(setTiktokAdAccounts(accessibleTiktokAdAccounts));
            if (accessibleTiktokAdAccounts.length === 0) {
              this.messageDispatcher.notifyUnimportant('message.tiktok_no_linkable_ad_account');
            }
          }
          return true;
        } catch (err) {
          this.errorPresenter.showAsNonFatal(err);
          throw err;
        } finally {
          this.store.dispatch(decrementFullScreenSpinnerNecessity());
        }
      }
    }
  };

  public integrateTiktokAdAccount = async (tiktokAdAccount: TiktokAdAccount): Promise<void> => {
    this.store.dispatch(incrementFullScreenSpinnerNecessity());
    try {
      const state: State = this.store.getState();
      const tokenId = state.integratedTiktokAdAccounts.tokenId;
      this.store.dispatch(setLoadingMessage('message.tiktok_now_integrating'));
      const isSuccess = await this.tiktokAdWebApi.integrateAdAccount(
        this.getCurrentOrganization().id,
        tiktokAdAccount.adAccountId,
        tokenId,
      );
      if (isSuccess) {
        this.messageDispatcher.notifyUnimportant('message.tiktok_integrated');
      } else {
        this.errorPresenter.showAsNonFatal(new Error('message.tiktok_integrated_failed'));
      }
    } catch (err) {
      this.errorPresenter.showAsNonFatal(err);
    } finally {
      this.store.dispatch(decrementFullScreenSpinnerNecessity());
    }
    this.fetchIntegratedTiktokAdAccounts();
  };

  public fetchIntegratedTiktokAdAccounts = async (orgId?: string): Promise<boolean | void> => {
    this.store.dispatch(incrementFullScreenSpinnerNecessity());
    this.store.dispatch(startIntegratedTiktokAdAccounts());

    try {
      const integratedTiktokAdAccounts = await this.tiktokAdWebApi.getAllIntegratedAdAccounts(
        orgId || this.getCurrentOrganization().id,
      );
      this.store.dispatch(setIntegratedTiktokAdAccounts(integratedTiktokAdAccounts));
      return true;
    } catch (err) {
      this.errorPresenter.showAsNonFatal(err);
    } finally {
      this.store.dispatch(decrementFullScreenSpinnerNecessity());
    }
  };

  public fetchUploadedTiktokCreatives = async (creativeId: string, orgId?: string): Promise<boolean | void> => {
    this.store.dispatch(incrementFullScreenSpinnerNecessity());

    try {
      const uploadedTiktokCreatives = await this.tiktokAdWebApi.getUploadedTiktokCreatives(
        orgId || this.getCurrentOrganization().id,
        [creativeId],
      );
      this.store.dispatch(setTiktokUploadedCreative(!!uploadedTiktokCreatives.length));
      return !!uploadedTiktokCreatives.length;
    } catch (err) {
      this.errorPresenter.showAsNonFatal(err);
    } finally {
      this.store.dispatch(decrementFullScreenSpinnerNecessity());
    }
  };

  public unlinkTiktokAdAccount = async (tiktokAdAccount: TiktokAdAccount): Promise<void> => {
    this.store.dispatch(incrementFullScreenSpinnerNecessity());
    this.store.dispatch(startIntegratedTiktokAdAccounts());
    try {
      const isSuccess = await this.tiktokAdWebApi.unlinkTiktokAdAccount(
        this.getCurrentOrganization().id,
        tiktokAdAccount.adAccountId,
      );
      if (isSuccess) {
        this.messageDispatcher.notifyUnimportant('message.tiktok_revoked');
      } else {
        this.errorPresenter.showAsNonFatal(new Error('message.tiktok_revoked_failed'));
      }
      this.store.dispatch(finishIntegratedTiktokCommunication());
    } catch (err) {
      this.errorPresenter.showAsFatal(err);
    } finally {
      this.store.dispatch(decrementFullScreenSpinnerNecessity());
    }
    this.fetchIntegratedTiktokAdAccounts();
  };

  // 連携tiktokアカウントにcreativeを送信する
  public sendCreative = async (
    deliverable: {
      id: string;
      creative: {
        id: string;
      };
    },
    tiktokAdAccount: TiktokAdAccount,
    projectId: string | null,
  ): Promise<void> => {
    this.store.dispatch(incrementFullScreenSpinnerNecessity());

    if (projectId) {
      try {
        this.store.dispatch(setLoadingMessage('message.creative_now_sending'));

        await this.tiktokAdWebApi.sendCreativeToTiktok(
          deliverable.creative.id,
          this.getCurrentOrganization().id,
          projectId,
          tiktokAdAccount.adAccountId,
        );

        this.messageDispatcher.notifyUnimportant('message.creative_has_sent_to_tiktok_ad_asset_library', {
          action: 'message.creative_has_sent_to_tiktok_ad_asset_library.action',
          onClickAction: () => this.transitionToTiktokAdAssetLibrary(tiktokAdAccount),
        });

        const uploadedAdPlatformKeys =
          this.store.getState().creativeSending.uploadedAdPlatformKeys.get(deliverable.id) || [];
        this.store.dispatch(setUploadedAdPlatformKeys(deliverable.id, [...uploadedAdPlatformKeys, 'tiktok']));
      } catch (err) {
        if (err.type == 'upload_creative-token_expired') {
          // アクセストークンが期限切れのため再認可/認証を行う
          this.messageDispatcher.notifyUnimportant('message.tiktok_upload_creative-token_expired', {
            action: 'reauthorize_tiktok_ad_account_dialog.send',
            onClickAction: () => this.fetchAd(),
          });
        } else {
          this.errorPresenter.showAsNonFatal(err);
        }
      } finally {
        this.store.dispatch(decrementFullScreenSpinnerNecessity());
      }
    }
  };

  private transitionToTiktokAdAssetLibrary = (tiktokAdAccount: TiktokAdAccount) => {
    const redirectURL = `https://ads.tiktok.com/i18n/library/video?aadvid=${tiktokAdAccount.adAccountId}`;
    window.open(`${redirectURL}`, '_blank');
  };

  private getCurrentOrganization(): OrganizationDetailFragment {
    const state: State = this.store.getState();

    let organization: OrganizationDetailFragment | null = null;
    if (state.lastAccessedOrganization) {
      organization = state.lastAccessedOrganization;
    } else {
      organization = state.availableOrganizations.items[0];
    }
    if (!organization) {
      throw Error('organization was not found');
    }
    this.store.dispatch(setLastAccessedOrganization(organization));
    return organization;
  }
}

export default IntegrateTiktokAdAccountUsecase;
